(() => {
  if (window.nordsignSharedCategories) return;
  window.nordsignSharedCategories = true;
  const roots = 'nordsign-flag-selector,nordsign-frame-selector,nordsign-range';
  const cardSelector = '[data-family],[data-frame],[data-range-card]';
  // Wait for browser scroll anchoring and the shared row layout before correcting position.
  const keepSectionInView = section => requestAnimationFrame(() => requestAnimationFrame(() => {
    if (!section.isConnected) return;
    const sticky = document.querySelector('sticky-header');
    const clearance = Math.max(16, Math.min(innerHeight / 3, sticky?.getBoundingClientRect().height || 0) + 16);
    const top = section.getBoundingClientRect().top;
    if (top < clearance || top > innerHeight - 80) window.scrollBy({top: top - clearance, behavior: 'instant'});
  }));
  window.nordsignKeepSectionInView = keepSectionInView;
  const disclosures = new WeakMap();
  let uid = 0;
  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    node.className = cls;
    if (text) node.textContent = text;
    return node;
  };
  const visible = node => node.getClientRects().length > 0;
  function action(node, text, primary = false) {
    if (!node) return;
    node.classList.add('ns-shared-action');
    node.classList.toggle('ns-shared-action--primary', primary);
    node.replaceChildren(el('span', '', text), el('span', 'ns-shared-arrow', '\u2192'));
    node.lastChild.setAttribute('aria-hidden', 'true');
  }
  function disclosure(details, messages) {
    if (!details || disclosures.has(details)) return;
    const section = el('section', `${details.className} ns-shared-spec`);
    const title = el('h3', '', messages.specifications);
    const content = el('div', 'ns-shared-spec__content');
    content.id = `SharedSpecs-${++uid}`;
    [...details.children].filter(n => n.tagName !== 'SUMMARY').forEach(n => content.append(n));
    content.querySelectorAll('dl').forEach(list => list.classList.add('ns-shared-spec__list'));
    const preview = el('div', 'ns-shared-spec__preview');
    preview.setAttribute('aria-hidden', 'true');
    const updatePreview = () => {
      preview.replaceChildren();
      [...content.querySelectorAll('dt')].forEach(dt => {
        const row = el('div', 'ns-shared-spec__fact');
        row.append(el('span', '', dt.textContent), el('span', '', dt.nextElementSibling?.textContent || ''));
        preview.append(row);
      });
      [...content.children].filter(n => n.matches('ul,p')).forEach(n => {
        if (n.tagName === 'UL') [...n.children].forEach(item => preview.append(el('p', '', item.textContent)));
        else preview.append(el('p', '', n.textContent));
      });
    };
    updatePreview();
    const toggle = el('button', 'ns-shared-spec__toggle');
    toggle.type = 'button';
    toggle.setAttribute('aria-controls', content.id);
    const label = el('span');
    const chevron = el('span', 'ns-shared-chevron');
    chevron.setAttribute('aria-hidden', 'true');
    toggle.append(label, chevron);
    let opened = false, fits = false, collapsedHeight = 48;
    const render = () => {
      const transferFocus = fits && document.activeElement === toggle;
      content.hidden = !opened && !fits;
      preview.hidden = opened || fits;
      toggle.hidden = fits;
      toggle.setAttribute('aria-expanded', String(opened && !fits));
      label.textContent = opened && !fits ? messages.hide : messages.show;
      if (transferFocus) {
        content.tabIndex = -1;
        content.focus({preventScroll: true});
      }
    };
    const setOpen = open => { opened = open; render(); };
    const fit = limit => {
      collapsedHeight = limit === null ? 48 : Math.max(48, limit);
      if (!visible(section)) return;
      preview.style.height = collapsedHeight + 'px';
      const hidden = content.hidden;
      content.hidden = false;
      const height = content.getBoundingClientRect().height;
      content.hidden = hidden;
      // A fully visible section needs neither an excerpt fade nor its disclosure button.
      fits = height <= collapsedHeight + 1;
      render();
    };
    section.append(title, preview, content, toggle);
    details.replaceWith(section);
    disclosures.set(section, {setOpen, fit});
    fit(null);
    let queued, width;
    const remeasure = () => {
      cancelAnimationFrame(queued);
      queued = requestAnimationFrame(() => {
        if (section.isConnected) fit(collapsedHeight);
      });
    };
    const resizeObserver = new ResizeObserver(entries => {
      const nextWidth = entries[0].contentRect.width;
      if (nextWidth !== width) { width = nextWidth; remeasure(); }
    });
    resizeObserver.observe(section);
    const contentObserver = new MutationObserver(records => {
      // Measuring temporarily changes the content container's hidden attribute.
      if (!records.some(record => record.target !== content || record.type !== 'attributes')) return;
      updatePreview();
      remeasure();
    });
    contentObserver.observe(content, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden']});
    section.addEventListener('load', remeasure, true);
    section.addEventListener('change', remeasure);
    document.fonts?.ready.then(remeasure);
    document.fonts?.addEventListener('loadingdone', remeasure);
    toggle.addEventListener('click', () => {
      if (section.closest('[data-shared-detail]')) {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        setOpen(open);
        if (!open) keepSectionInView(section);
        return;
      }
      const card = section.closest(cardSelector);
      const root = section.closest(roots);
      const top = card.getBoundingClientRect().top;
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      const row = [...root.querySelectorAll(cardSelector)].filter(c => visible(c) && Math.abs(c.getBoundingClientRect().top - top) < 2);
      row.forEach(c => c.querySelectorAll('.ns-shared-spec').forEach(s => { if (visible(s)) disclosures.get(s)?.setOpen(open); }));
      if (!open) keepSectionInView(section);
    });
    return section;
  }
  function mediaMeta(media, use) {
    if (!media) return;
    media.classList.add('ns-shared-media');
    const meta = el('div', 'ns-shared-media-meta');
    meta.append(el('span', '', use || ''));
    const dots = media.querySelector('.ns-product-preview__dots');
    if (dots) {
      // Keep dots inside their custom element so its gallery listeners remain valid.
      dots.classList.add('ns-shared-dots');
      media.classList.add('ns-shared-media--gallery');
    }
    media.after(meta);
  }
  function flag(card, m) {
    card.querySelector('.ns-flag-family__heading')?.classList.add('ns-shared-identity');
    const top = el('div', 'ns-shared-top');
    [...card.children].filter(n => !n.matches('[data-model]')).forEach(n => top.append(n));
    card.prepend(top);
    card.querySelectorAll('[data-model]').forEach(model => {
      mediaMeta(model.querySelector('.ns-flag__media'), model.querySelector('.ns-flag__sizes')?.textContent);
      flagConfig(model.querySelector('nordsign-drop-config'), m);
    });
  }
  function flagConfig(config, m) {
      if (!config) return;
      const controls = config.querySelector('[data-drop-controls]');
      controls.classList.add('ns-shared-controls');
      const pack = controls.querySelector('.ns-drop__package');
      const measurement = el('div', 'ns-shared-measurement');
      pack.before(measurement);
      const includes = el('div', 'ns-shared-includes');
      pack.after(includes);
      const template = el('a', 'ns-shared-action');
      config.querySelector('.ns-drop__quote').before(template);
      const sync = () => {
        const panel = [...config.querySelectorAll('[data-drop-size]')].find(p => !p.hidden);
        if (!panel) return;
        measurement.replaceChildren(panel.querySelector('.ns-drop__measurements').cloneNode(true));
        const copy = [...panel.querySelectorAll('[data-set-copy],[data-print-copy]')].find(n => !n.hidden);
        includes.textContent = copy?.querySelector('p')?.textContent || '';
        const link = panel.querySelector('.ns-drop__download');
        template.href = link.href;
        if (link.target) template.target = link.target; else template.removeAttribute('target');
        template.rel = 'noopener';
        action(template, link.hasAttribute('download') ? m.download : m.request);
      };
      config.addEventListener('change', sync);
      sync();
      config.querySelector('.ns-drop__selection').hidden = true;
      config.querySelector('.ns-drop__help').hidden = true;
      disclosure(config.querySelector('.ns-drop__specs'), m);
      action(config.querySelector('.ns-drop__quote'), m.quote, true);
  }
  function frame(card, m) {
    const main = card.querySelector('.ns-frame-card__main');
    const identity = el('div', 'ns-shared-identity');
    main.before(identity);
    identity.append(main.querySelector('span'), main.querySelector('h2'));
    const compare = card.querySelector('[data-selection]');
    if (compare) identity.prepend(compare);
    const config = card.querySelector('nordsign-frame-config');
    const difference = config.querySelector('.ns-frames__difference');
    identity.append(difference);
    const overview = card.querySelector('.ns-frame-card__overview');
    identity.after(overview);
    const top = el('div', 'ns-shared-top');
    identity.before(top);
    top.append(identity, overview);
    mediaMeta(main, m.indoor);
    frameConfig(config, m);
    card.querySelector('.ns-frame-card__details').hidden = true;
  }
  function frameConfig(config, m, initializeDefaults = true) {
    const form = config.querySelector('form');
    const controls = el('div', 'ns-shared-controls');
    form.prepend(controls);
    [...form.children].filter(n => n.matches('fieldset,.ns-frame-config__size,[data-replacement-note]')).forEach(n => controls.append(n));
    const portrait = form.querySelector('[name=orientation][value=Portrait]');
    if (portrait && initializeDefaults) {
      portrait.checked = true;
      // Starting enquiry dimensions, not a claim that custom frames have a stock size.
      const start = (value, min, max) => Math.min(Number(max) || Infinity, Math.max(Number(min) || 0.1, value));
      form.elements.width_cm.value = String(start(100, config.dataset.minShort, config.dataset.maxShort));
      form.elements.height_cm.value = String(start(150, config.dataset.minLong, config.dataset.maxLong));
      form.querySelectorAll('[data-size-limits],.ns-frames__hint:not([data-size-error]):not([data-replacement-note])').forEach(n => n.hidden = true);
      portrait.dispatchEvent(new Event('change', {bubbles: true}));
    }
    const template = config.querySelector('[data-template]');
    form.querySelector('.ns-frames__quote').before(template);
    const syncTemplate = () => action(template, template.dataset.directDownload === 'true' ? m.download : m.request);
    config.addEventListener('templatechange', syncTemplate);
    syncTemplate();
    disclosure(config.querySelector('.ns-frame-config__specs'), m);
    action(form.querySelector('.ns-frames__quote'), m.quote, true);
  }
  function range(card, m) {
    const body = card.querySelector('.ns-range__card-body');
    const identity = el('div', 'ns-shared-identity ns-shared-top');
    card.prepend(identity);
    ['.ns-range__card-top','h2','[data-summary]'].forEach(s => { const n = body.querySelector(s); if (n) identity.append(n); });
    const difference = body.querySelector('.ns-range__difference');
    if (difference) identity.append(difference);
    const media = card.querySelector('nordsign-product-preview') || card.querySelector('.ns-range__image');
    const use = card.dataset.useLabel || card.querySelector('nordsign-drop-config [data-spec="Use"]')?.textContent || (card.querySelector('nordsign-frame-config') ? m.indoor : '');
    mediaMeta(media, use);
    const flagOptions = body.querySelector('nordsign-drop-config');
    const frameOptions = body.querySelector('nordsign-frame-config');
    if (flagOptions || frameOptions) {
      if (flagOptions) flagConfig(flagOptions, m);
      else {
        frameOptions.querySelector('.ns-frames__difference')?.remove();
        frameConfig(frameOptions, m);
      }
      body.querySelector('.ns-range__links').hidden = true;
      body.querySelector('.ns-range__facts').hidden = true;
      return;
    }
    const oldDetails = body.querySelector(':scope > details');
    const options = oldDetails?.querySelector('nordsign-range-options');
    if (!options) return;
    oldDetails.replaceWith(options);
    body.querySelector('.ns-range__links').hidden = true;
    body.querySelector('.ns-range__facts').hidden = true;
    rangeConfig(options, m, card);
  }
  function rangeConfig(options, m, card) {
    const form = options.querySelector('form');
    const controls = el('div', 'ns-shared-controls');
    form.prepend(controls);
    [...form.children].filter(n => n !== controls && !n.matches('input[type=hidden],button[type=submit],[data-template],script')).forEach(n => controls.append(n));
    const details = options.querySelector(':scope > details') || el('details', 'ns-range__details');
    [...options.children].filter(n => n !== form && n !== details).forEach(n => details.append(n));
    const template = form.querySelector('[data-template]');
    form.insertBefore(details, form.querySelector('button[type=submit]'));
    const spec = disclosure(details, m);
    const quote = form.querySelector('button[type=submit]');
    quote.before(template);
    action(template, m.request);
    action(quote, m.quote, true);
    // Document lookup remains inside the original custom element and keeps its search behaviour.
    const documents = spec.querySelector('.ns-range-documents');
    if (documents) documents.classList.add('ns-shared-documents');
    form.querySelectorAll('select[data-size],select[data-package]').forEach(select => optionButtons(select, !card.hasAttribute('data-shared-detail-card')));
    const templateDocs = window.nordsignSharedTemplates?.[form.dataset.handle] || [];
    const dimensionKey = text => (text.match(/\d+(?:[.,]\d+)?/g) || []).map(n => Number(n.replace(',','.'))).join('x');
    const verifiedTemplates = JSON.parse(form.querySelector('[data-verified-templates]')?.textContent || '{}');
    const syncTemplate = () => {
      const packageChoice = form.querySelector('[data-package]')?.value || '';
      if (form.closest('[data-category-only]')) {
        template.hidden = form.dataset.noArtwork === 'true' || packageChoice === 'Support bar only';
        if (template.hidden) return;
      }
      const verified = verifiedTemplates[packageChoice] || template.dataset.verifiedTemplate;
      if (verified) {
        template.href = verified;
        template.target = '_blank';
        template.rel = 'noopener';
        template.dataset.directDownload = 'true';
        action(template, m.download);
        return;
      }
      const size = form.querySelector('[data-size]')?.value || '';
      const matching = templateDocs.filter(d => dimensionKey(d.label) === dimensionKey(size));
      const chosen = matching.length === 1 ? matching[0] : !size && templateDocs.length === 1 ? templateDocs[0] : null;
      const link = chosen && [...spec.querySelectorAll('a[href]')].find(a => a.href.includes(chosen.asset));
      template.removeAttribute('target');
      if (link) {
        template.href = link.href;
        template.target = '_blank';
        template.rel = 'noopener';
        template.dataset.directDownload = 'true';
        action(template, m.download);
      } else {
        delete template.dataset.directDownload;
        action(template, templateDocs.length ? m.templates : m.request);
      }
    };
    form.addEventListener('change', syncTemplate);
    form.addEventListener('input', syncTemplate);
    template.addEventListener('click', event => {
      // Existing enquiry handlers update this link first; restore only a verified document match.
      syncTemplate();
      if (templateDocs.length && !template.dataset.directDownload) {
        event.preventDefault();
        if (spec.querySelector('.ns-shared-spec__toggle').getAttribute('aria-expanded') !== 'true') spec.querySelector('.ns-shared-spec__toggle').click();
        const target = spec.querySelector('.ns-range-documents a');
        target?.focus();
      }
    });
    // Run after the range option handler at the card level has updated the enquiry context.
    card.addEventListener('click', event => { if(event.target.closest('[data-template]')) syncTemplate(); });
    syncTemplate();
    if (card.hasAttribute('data-category-only')) {
      card.querySelectorAll('a[href*="#RangeCard-"]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        const toggle = spec.querySelector('.ns-shared-spec__toggle');
        if (toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
        toggle.focus({preventScroll: true});
        spec.scrollIntoView({block: 'nearest', behavior: 'smooth'});
      }));
    }
  }
  function optionButtons(select, initializeDefaults = true) {
    const options = [...select.options].filter(o => o.value);
    if (!options.length) return;
    if (initializeDefaults && !options.some(option => option.value === select.value && !option.disabled)) select.value = (options.find(option => !option.disabled) || options[0]).value;
    select.dispatchEvent(new Event('change', {bubbles:true}));
    if (options.length > 4) return;
    const label = select.closest('label');
    const fieldset = el('fieldset', select.hasAttribute('data-package') ? 'ns-shared-packages' : 'ns-shared-segments');
    fieldset.append(el('legend', '', [...label.childNodes].filter(n => n.nodeType === 3).map(n => n.textContent).join('').trim()));
    const name = `shared-option-${++uid}`;
    const sync = () => fieldset.querySelectorAll('input').forEach((input,i) => {input.checked=select.value===options[i].value;input.disabled=options[i].disabled;});
    options.forEach(option => {
      const item = el('label');
      const input = document.createElement('input');
      input.type='radio';input.name=name;input.value=option.value;
      item.append(input,el('span','',option.textContent));
      input.addEventListener('change',()=>{select.value=input.value;select.dispatchEvent(new Event('change',{bubbles:true}));sync();});
      fieldset.append(item);
    });
    label.hidden=true;
    label.after(fieldset);
    select.form.addEventListener('change',sync);
    sync();
  }
  function init(scope = document) {
    const data = document.querySelector('[data-shared-category-messages]');
    if (!data) return;
    const m = JSON.parse(data.textContent);
    const decoder = document.createElement('textarea');
    Object.keys(m).forEach(key => {
      decoder.innerHTML = m[key];
      m[key] = decoder.value;
    });
    scope.querySelectorAll('[data-shared-detail]').forEach(root => {
      if (root.dataset.sharedReady) return;
      root.dataset.sharedReady = 'true';
      const card = root.querySelector('[data-shared-detail-card]');
      const flag = card.querySelector('nordsign-drop-config');
      const frame = card.querySelector('nordsign-frame-config');
      const range = card.querySelector('nordsign-range-options');
      if (flag) flagConfig(flag, m);
      else if (frame) frameConfig(frame, m, false);
      else if (range) rangeConfig(range, m, card);
    });
    scope.querySelectorAll(roots).forEach(root => {
      if (root.classList.contains('ns-shared')) return;
      root.classList.add('ns-shared');
      const header = root.querySelector('.ns-flags__intro,.ns-frames__intro,.ns-range__heading');
      header?.classList.add('ns-shared-heading');
      const help = header?.querySelector(':scope > a');
      if (help) action(help, m.choose);
      const guide = root.querySelector('.ns-flags__guide,.ns-frames__guide,.ns-range__guide');
      if (guide) guide.querySelector('summary').textContent = m.guide;
      root.querySelectorAll('[data-reset],[data-range-reset]').forEach(n => n.textContent = m.clear);
      const cards = [...root.querySelectorAll(cardSelector)];
      cards.forEach(card => {
        card.classList.add('ns-shared-card');
        if (card.matches('[data-family]')) flag(card, m);
        else if (card.matches('[data-frame]')) frame(card, m);
        else range(card, m);
      });
      const grid = root.querySelector('.ns-flags__grid,.ns-frames__grid,.ns-range__grid');
      grid?.classList.add('ns-shared-grid');
      let queued;
      const align = () => {
        cancelAnimationFrame(queued);
        queued = requestAnimationFrame(() => {
          const active = cards.filter(visible);
          active.forEach(c => {
            c.querySelectorAll('.ns-shared-top,.ns-shared-media-meta,.ns-shared-controls').forEach(n => n.style.minHeight = '');
            c.querySelectorAll('.ns-shared-spec').forEach(n => disclosures.get(n)?.fit(null));
          });
          if (innerWidth > 749) {
            const rows = new Map();
            // Layout offsets stay stable while deep links and scroll anchoring move the viewport.
            active.forEach(c => { const top = c.offsetTop; const row = rows.get(top) || []; row.push(c); rows.set(top, row); });
            // Keep titles and galleries aligned; options and specifications flow per card.
            rows.forEach(row => ['.ns-shared-top','.ns-shared-media-meta'].forEach(selector => {
              const blocks = row.flatMap(c => [...c.querySelectorAll(selector)].filter(visible));
              const height = Math.max(0, ...blocks.map(n => n.getBoundingClientRect().height));
              blocks.forEach(n => n.style.minHeight = `${height}px`);
            }));
            rows.forEach(row => {
              const specs = row.flatMap(card => [...card.querySelectorAll('.ns-shared-spec')].filter(visible).map(spec => ({spec, top: spec.getBoundingClientRect().top - card.getBoundingClientRect().top})));
              const bottomStart = Math.max(0, ...specs.map(item => item.top));
              specs.forEach(item => disclosures.get(item.spec)?.fit(48 + bottomStart - item.top));
            });

          }
          const result = root.querySelector('[data-count],[data-range-result]');
          if (result) result.textContent = (active.length === cards.length && m.results_all ? m.results_all : m.results).replace('__count__', active.length).replace('__total__', cards.length);
        });
      };
      root.addEventListener('change', align);
      root.addEventListener('input', align);
      root.addEventListener('reset', align);
      root.addEventListener('click', align);
      root.addEventListener('load', align, true);
      window.addEventListener('resize', align);
      document.fonts.ready.then(align);
      align();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
